// apps/web/src/context/LocaleContext.tsx
import * as React from 'react';

type Locale = 'en' | 'de';

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, fallback?: string) => string;
  available: Locale[];
};

const STORAGE_KEY = 'app_locale';
const DEFAULT_LOCALE: Locale = 'en';
const AVAILABLE: Locale[] = ['en', 'de'];

const translations: Record<Locale, Record<string, string>> = {
  en: {},
  de: {
    // ── Started Training screen ──
    'Distance (m)': 'Distanz (m)',
    'Reps': 'Wdh.',
    'Weight (kg)': 'Gewicht (kg)',
    'Round': 'Runde',
    'Set': 'Satz',
    'EMOM Countdown': 'EMOM-Countdown',
    'AMRAP Countdown': 'AMRAP-Countdown',
    'Time Cap': 'Zeitlimit',
    'Workout Timer': 'Workout-Timer',
    'Next Method': 'Nächste Methode',
    'Finish Workout': 'Workout beenden',
    'Next Exercise': 'Nächste Übung',
    'Next up': 'Als Nächstes',
    'Last set': 'Letzter Satz',
    'Save Set': 'Satz speichern',
    'Set Done': 'Satz erledigt',
    'Re-time': 'Neu stoppen',
    // The footer's left-hand number. Its right-hand neighbour ("Set pause" /
    // "Rest time") is still raw English in StartedTrainingCard, so this is the
    // only one of the three that is translated at all.
    'Total pause': 'Pause gesamt',
    'Method Complete': 'Methode abgeschlossen',
    'Next method ready': 'Nächste Methode bereit',
    'Equipment needed': 'Benötigte Ausrüstung',
    'Start next Method': 'Nächste Methode starten',
    'Rounds completed': 'Runden geschafft',
    '+ Round': '+ Runde',
    'AMRAP time is up.': 'AMRAP-Zeit ist um.',
    'Finish EMOM': 'EMOM beenden',
    'Finish AMRAP': 'AMRAP beenden',
    'No exercise': 'Keine Übung',
    'No exercises': 'Keine Übungen',
    'Time is up': 'Zeit ist um',
    'Next up in': 'Nächste in',
    'Mute sounds': 'Töne stumm',
    'Unmute sounds': 'Töne an',
    // ── AI Planner method labels / preview ──
    'min': 'Min.',
    'cap': 'Limit',
    'Rounds for Time': 'Runden auf Zeit',
    'Ladder': 'Leiter',
    'Main': 'Haupt',
    'Finisher': 'Finisher',
    'dropset': 'Dropsatz',
    'superset': 'Supersatz',
    'Weights suggested from your logged rep-maxes — adjust any time.':
      'Gewichte aus deinen erfassten Maxima vorgeschlagen — jederzeit anpassbar.',
    // ── Streaks & PR celebrations ──
    'week streak': 'Wochen-Serie',
    'best': 'beste',
    'workout this week': 'Workout diese Woche',
    'workouts this week': 'Workouts diese Woche',
    'Train this week to keep it going': 'Trainiere diese Woche, um sie fortzusetzen',
    'Start a new streak': 'Starte eine neue Serie',
    'Nice — keep it up next week too': 'Stark — mach nächste Woche weiter so',
    'Complete a workout this week': 'Schließe diese Woche ein Workout ab',
    'weeks in a row! Keep it up.': 'Wochen in Folge! Weiter so.',
    'New PR!': 'Neuer PR!',
    'new PRs!': 'neue PRs!',
    'Tap to dismiss': 'Zum Schließen tippen',
    // ── Coach: Profile tab ──
    'Upload picture': 'Bild hochladen',
    'Remove picture': 'Bild entfernen',
    'Photo': 'Foto',
    'Picture file is too large': 'Bilddatei ist zu groß',
    // Text Planner (German i18n)
    'Create': 'Erstellen',
    'Edit': 'Bearbeiten',
    'Check': 'Prüfen',
    'Text Planner View': 'Text-Planer-Ansicht',
    'Structure Example': 'Struktur-Beispiel',
    'Use a date, `today`, or `tomorrow`, then workout name, method, repeating exercise blocks, and notes.': 'Verwende ein Datum, `today` oder `tomorrow`, dann Workout-Name, Methode, sich wiederholende Übungsblöcke und Notizen.',
    'Line': 'Zeile',
    '1. `YYYY/MM/DD` or `YYYY/MM/DD HH:mm` or `today` / `tomorrow`': '1. `YYYY/MM/DD` oder `YYYY/MM/DD HH:mm` oder `today` / `tomorrow`',
    '2. Workout Name': '2. Workout-Name',
    '3. Method': '3. Methode',
    '4. Exercise': '4. Übung',
    '5. Sets': '5. Sätze',
    '6. Reps/Weight like `12/50 10/60 8/70`': '6. Wdh./Gewicht wie `12/50 10/60 8/70`',
    '7. Repeat lines 4-6 for more exercises, then finish with notes.': '7. Zeilen 4-6 für weitere Übungen wiederholen, dann mit Notizen abschließen.',
    'Method Autocomplete': 'Methoden-Autovervollständigung',
    'Exercise Autocomplete': 'Übungs-Autovervollständigung',
    'Autocomplete': 'Autovervollständigung',
    'Click a method to replace the current method line.': 'Klicke auf eine Methode, um die aktuelle Methoden-Zeile zu ersetzen.',
    'Click an exercise to replace the current exercise line.': 'Klicke auf eine Übung, um die aktuelle Übungs-Zeile zu ersetzen.',
    'Move the cursor to line 3 or an exercise line to get suggestions.': 'Bewege den Cursor auf Zeile 3 oder eine Übungs-Zeile, um Vorschläge zu erhalten.',
    'No matches for the current line yet.': 'Noch keine Treffer für die aktuelle Zeile.',
    'Validation': 'Validierung',
    'Start typing or insert the example to see a parsed preview.': 'Tippe los oder füge das Beispiel ein, um eine Vorschau zu sehen.',
    'Valid training text': 'Gültiger Trainingstext',
    'Schedule:': 'Termin:',
    'Workout Name:': 'Workout-Name:',
    'Method:': 'Methode:',
    'Exercises:': 'Übungen:',
    'Using:': 'Verwendet:',
    'Share this training with friends': 'Dieses Training mit Freunden teilen',
    'The Text Planner creates a normal planned training. You can still edit it later in the planner UI.': 'Der Text-Planer erstellt ein normales geplantes Training. Du kannst es später im Planer noch bearbeiten.',
    'Missing lines': 'Fehlende Zeilen',
    'Wrong lines': 'Falsche Zeilen',
    'Example': 'Beispiel',
    'Clear': 'Leeren',
    'Gray lines are missing lines Probell can fill. Red lines are corrected or still wrong.': 'Graue Zeilen sind fehlende Zeilen, die Probell ergänzen kann. Rote Zeilen wurden korrigiert oder sind noch falsch.',
    '[Nothing to check yet]': '[Noch nichts zu prüfen]',
    // Navigation / tabs
    'Create Training': 'Training erstellen',
    'Planned Training': 'Geplante Trainings',
    Create: 'Erstellen',
    Planned: 'Geplant',
    Coach: 'Coach',
    Completed: 'Geschafft',
    'Plan Shop': 'Plan-Shop',
    More: 'Mehr',
    'Workout Plan Shop': 'Trainingspläne',
    'Training Methods': 'Trainingsmethoden',
    'Workout Manager': 'Workout-Manager',
    'Planned Training Preview': 'Geplante Trainingsvorschau',
    'Next two weeks': 'Naechste zwei Wochen',
    'Selected Training': 'Ausgewaehltes Training',
    'Selected Trainings': 'Ausgewaehlte Trainings',
    'Add planned trainings': 'Geplante Trainings hinzufuegen',
    'Search planned trainings': 'Geplante Trainings suchen',
    'No planned trainings found.': 'Keine geplanten Trainings gefunden.',
    'Already on this day': 'Bereits an diesem Tag',
    'Added to calendar': 'Zum Kalender hinzugefuegt',
    'Failed to add planned training': 'Geplantes Training konnte nicht hinzugefuegt werden',
    'Edit time': 'Zeit bearbeiten',
    'Time updated': 'Zeit aktualisiert',
    'Failed to update time': 'Zeit konnte nicht aktualisiert werden',
    'Remove from calendar?': 'Aus dem Kalender entfernen?',
    'Removed from calendar': 'Aus dem Kalender entfernt',
    'Failed to remove from calendar': 'Aus dem Kalender konnte nicht entfernt werden',
    'Select a planned training': 'Geplantes Training auswaehlen',
    'Select a day to choose planned trainings': 'Tag waehlen, um Trainings auszuwaehlen',
    'Select planned trainings for the chosen day': 'Trainings fuer den gewaehlten Tag auswaehlen',
    'Planned Trainings on': 'Geplante Trainings am',
    'No planned trainings on this day.': 'Keine geplanten Trainings an diesem Tag.',
    'No planned trainings in the next two weeks.': 'Keine geplanten Trainings in den naechsten zwei Wochen.',
    Methods: 'Methoden',
    Exercises: 'Übungen',
    'Create New Training': 'Neues Training erstellen',
    'Open Planner': 'Planer öffnen',
    Planner: 'Planer',
    'Self Created': 'Selbst erstellt',
    'Rescheduled': 'Erneut geplant',
    'Coach Plans': 'Coach-Plaene',
    Booking: 'Buchung',
    'Coach Friend ID': 'Coach-Freundes-ID',
    'Enter Coach Friend ID': 'Coach-Freundes-ID eingeben',
    'Add the Coach Friend ID.': 'Coach-Freundes-ID eingeben.',
    'You can find the Coach Friend ID in your Workout Manager from your Coach (ask your Trainer).':
      'Die Coach-Freundes-ID findest du im Workout Manager deines Coaches (frag deinen Trainer).',
    'Connected Coach ID': 'Verbundenes Coach-ID',
    'Coach connected': 'Coach verbunden',
    'Add your Coach Friend ID to view Coach Plans.': 'Coach-Freundes-ID eingeben, um Coach-Plaene zu sehen.',
    Connect: 'Verbinden',
    Update: 'Aktualisieren',
    Clear: 'Leeren',
    'Bought Plans': 'Gekaufte Pläne',
    'Friend Plans': 'Freundesplaene',
    'Share with Friends': 'Mit Freunden teilen',
    'Shared by': 'Geteilt von',

    // Locks / auth prompts
    'Login to view and manage planned trainings':
      'Bitte einloggen, um geplante Trainings zu sehen und zu verwalten',
    'Login to view coach plans': 'Bitte einloggen, um Coach-Plaene zu sehen',
    "Login to view today's training": 'Bitte einloggen, um das heutige Training zu sehen',
    'Login to view your completed trainings': 'Bitte einloggen, um abgeschlossene Trainings zu sehen',
    'Login to buy or use workout plans': 'Bitte einloggen, um Trainingspläne zu kaufen oder zu nutzen',
    'Login to browse methods': 'Bitte einloggen, um Methoden zu durchsuchen',
    'Login to browse exercises': 'Bitte einloggen, um Übungen zu durchsuchen',
    'Login to create a training': 'Bitte einloggen, um ein Training zu erstellen',
    'Login to use this feature': 'Zum Nutzen bitte einloggen',
    'Login to use Workout Manager': 'Bitte einloggen, um den Workout-Manager zu nutzen',
    Login: 'Login',
    Close: 'Schließen',

    // Header
    Light: 'Hell',
    Dark: 'Dunkel',
    Profile: 'Profil',
    'Training Stats': 'Trainingsstatistiken',
    'Workout Details': 'Workout-Details',
    Friends: 'Freunde',
    Logout: 'Abmelden',
    'No user is currently logged in.': 'Kein Benutzer ist angemeldet.',
    'Your Equipment': 'Deine Ausstattung',
    'Coach Name': 'Coach Name',
    'Enter coach name': 'Coach Name eingeben',
    'Coach URL': 'Coach URL',
    'Enter coach URL': 'Coach URL eingeben',
    Logo: 'Logo',
    'Upload Logo': 'Logo hochladen',
    'Remove Logo': 'Logo entfernen',
    'Coach QR Code': 'Coach-QR-Code',
    Address: 'Adresse',
    'Enter address': 'Adresse eingeben',
    'Failed to save Workout Manager': 'Workout-Manager konnte nicht gespeichert werden',
    'Logo file is too large': 'Logo-Datei ist zu gross',
    'Profile picture': 'Profilbild',
    'Upload Photo': 'Foto hochladen',
    'Remove Photo': 'Foto entfernen',
    'Profile picture file is too large': 'Profilbild ist zu gross',
    'Failed to upload profile picture': 'Profilbild konnte nicht hochgeladen werden',
    'Unlock this Feature': 'Funktion freischalten',
    Unlocked: 'Freigeschaltet',
    Saving: 'Speichern…',
    Saved: 'Gespeichert',
    'Sets & Reps, X min AMRAP and EMOM X Min are the only methods you can use at the moment.':
      'Sets & Reps, X Min AMRAP und EMOM X Min sind die einzigen Methoden, die du aktuell nutzen kannst.',
    'User ID': 'Benutzer-ID',
    'Language: English': 'Sprache: Englisch',
    'Language: German': 'Sprache: Deutsch',
    'Switch to German': 'Zu Deutsch wechseln',
    'Switch to English': 'Zu Englisch wechseln',
    'Toggle theme': 'Theme umschalten',
    'Friend ID': 'Freundes-ID',
    'Add Friend': 'Freund hinzufuegen',
    'Enter Friend ID': 'Freundes-ID eingeben',
    'Share this ID with friends': 'Teile diese ID mit Freunden',
    'Your Friends': 'Deine Freunde',
    'No friends yet.': 'Noch keine Freunde.',
    'Friend added': 'Freund hinzugefuegt',
    Accept: 'Akzeptieren',
    Decline: 'Ablehnen',
    'Failed to add friend': 'Freund konnte nicht hinzugefuegt werden',
    'Failed to accept friend': 'Freund konnte nicht akzeptiert werden',
    'Failed to decline friend': 'Freund konnte nicht abgelehnt werden',
    'Request pending': 'Anfrage ausstehend',
    'Friends since': 'Freunde seit',
    'Failed to load friends': 'Freunde konnten nicht geladen werden',
    'Failed to load planned trainings': 'Geplante Trainings konnten nicht geladen werden',
    'Failed to load friend plans': 'Freundesplaene konnten nicht geladen werden',
    'Please sign in to view coach plans': 'Bitte einloggen, um Coach-Plaene zu sehen',
    'Failed to load coach plans': 'Coach-Plaene konnten nicht geladen werden',
    'No coach plans scheduled.': 'Keine Coach-Plaene geplant.',
    Participants: 'Teilnehmer',
    Unlimited: 'Unbegrenzt',
    'Participant limit updated': 'Teilnehmerlimit aktualisiert',
    'Failed to update participant limit': 'Teilnehmerlimit konnte nicht aktualisiert werden',
    'Invalid participant limit': 'Ungueltiges Teilnehmerlimit',
    'Invalid booking window': 'Ungültiges Buchungsfenster',
    'Booking window too short': 'Buchungsfenster zu kurz',
    'Failed to update booking window': 'Buchungsfenster konnte nicht aktualisiert werden',
    'Booking window updated': 'Buchungsfenster aktualisiert',
    Book: 'Buchen',
    'Cancel booking': 'Buchung stornieren',
    Full: 'Voll',
    'Not available': 'Nicht verfügbar',
    'Workout booked': 'Workout gebucht',
    'Booking cancelled': 'Buchung storniert',
    'Workout is full': 'Workout ist voll',
    'Booking not open yet': 'Buchung noch nicht geöffnet',
    'Booking closed': 'Buchung geschlossen',
    'Booking unavailable': 'Buchung nicht verfügbar',
    'Booking opens at': 'Buchung öffnet am',
    'Failed to book workout': 'Workout konnte nicht gebucht werden',
    'Failed to cancel booking': 'Buchung konnte nicht storniert werden',
    'Please sign in to book workouts': 'Bitte einloggen, um Workouts zu buchen',
    'Edit booking': 'Buchung bearbeiten',
    'Bookable from': 'Buchbar ab',
    'Bookable anytime': 'Jederzeit buchbar',
    'Bookable from (hours)': 'Buchbar ab (Stunden)',
    'Hours before': 'Stunden vorher',
    Anytime: 'Jederzeit',
    Copy: 'Kopieren',
    Copied: 'Kopiert',
    'Copy failed': 'Kopieren fehlgeschlagen',
    Add: 'Hinzufuegen',
    'Adding.': 'Fuege hinzu.',

    // Auth modal/panel
    'Sign in': 'Anmelden',
    'Sign in to Your Account': 'In dein Konto einloggen',
    'Create Your Account': 'Konto erstellen',
    'Sign in with Google': 'Mit Google anmelden',
    or: 'oder',
    'Email Address': 'E-Mail-Adresse',
    Password: 'Passwort',
    'Forgotten password?': 'Passwort vergessen?',
    'Sign In': 'Anmelden',
    'Sign Up': 'Registrieren',
    'Need an Account?': 'Noch kein Konto?',
    'Sign up': 'Registrieren',
    'Already have an Account?': 'Schon ein Konto?',
    'Sign in to continue': 'Zum Fortfahren anmelden',
    'Check your email to verify your account.': 'Bitte E-Mails prüfen und Konto bestätigen.',
    'Invalid login credentials': 'Ungültige Anmeldedaten',
    'Please enter your email first.': 'Bitte zuerst die E-Mail eingeben.',
    'If an account exists for this email, a reset link has been sent.':
      'Falls ein Konto existiert, wurde ein Reset-Link per E-Mail gesendet.',
    'This email is already registered. Sign in instead, or use "Forgotten password?".':
      'Diese E-Mail ist bereits registriert. Bitte anmelden oder „Passwort vergessen?" verwenden.',
    'Your email is not verified yet. Check your inbox, or resend the link below.':
      'Deine E-Mail ist noch nicht bestätigt. Bitte Postfach prüfen oder den Link unten erneut senden.',
    'Resend verification email': 'Bestätigungs-E-Mail erneut senden',
    'Confirm Password': 'Passwort bestätigen',
    'Passwords do not match.': 'Passwörter stimmen nicht überein.',
    'Verification email sent again. It can take a few minutes — check your spam folder too.':
      'Bestätigungs-E-Mail erneut gesendet. Es kann einige Minuten dauern — bitte auch den Spam-Ordner prüfen.',

    // Auth callback / password reset
    'Signing you in…': 'Du wirst angemeldet…',
    'Sign-in failed': 'Anmeldung fehlgeschlagen',
    'Sign-up failed': 'Registrierung fehlgeschlagen',
    'Sign-in was cancelled.': 'Anmeldung wurde abgebrochen.',
    'Back to sign in': 'Zurück zur Anmeldung',
    'This link has expired or was already used. Request a new one and open it right away.':
      'Dieser Link ist abgelaufen oder wurde bereits verwendet. Bitte einen neuen anfordern und sofort öffnen.',
    'The sign-in link did not work. It may have expired — request a new one and open it right away.':
      'Der Anmeldelink hat nicht funktioniert. Möglicherweise ist er abgelaufen — bitte einen neuen anfordern und sofort öffnen.',
    'Choose a new password': 'Neues Passwort wählen',
    'New password': 'Neues Passwort',
    'Save new password': 'Neues Passwort speichern',
    'Password updated. Signing you in…': 'Passwort aktualisiert. Du wirst angemeldet…',
    'Password must be at least 6 characters.':
      'Das Passwort muss mindestens 6 Zeichen lang sein.',
    'At least 6 characters.': 'Mindestens 6 Zeichen.',
    'Could not update the password.': 'Das Passwort konnte nicht geändert werden.',
    'This reset link is no longer valid': 'Dieser Link ist nicht mehr gültig',
    'Reset links can only be used once and expire after a while. Request a new one from the sign-in screen.':
      'Reset-Links können nur einmal verwendet werden und laufen nach einer Weile ab. Bitte auf der Anmeldeseite einen neuen anfordern.',
    'Saving…': 'Speichern…',

    // Auth panel polish
    'Sign up with Apple': 'Mit Apple registrieren',
    'Sign up with Google': 'Mit Google registrieren',
    'Signing in…': 'Anmeldung läuft…',
    'Creating account…': 'Konto wird erstellt…',
    'Show password': 'Passwort anzeigen',
    'Hide password': 'Passwort verbergen',
    'Too many attempts. Please wait a minute and try again.':
      'Zu viele Versuche. Bitte eine Minute warten und erneut versuchen.',
    'Please enter a valid email address.': 'Bitte eine gültige E-Mail-Adresse eingeben.',
    'By creating an account you agree to our': 'Mit der Registrierung akzeptierst du unsere',
    Terms: 'Nutzungsbedingungen',
    and: 'und',

    // General UI
    Loading: 'Laden…',
    'Loading.': 'Laden…',
    'Calendar view': 'Kalenderansicht',
    Delete: 'Löschen',
    Remove: 'Entfernen',
    Edit: 'Bearbeiten',
    'List view': 'Listenansicht',
    Refresh: 'Aktualisieren',
    Reset: 'Zurücksetzen',
    Search: 'Suche',
    'Search exercises': 'Übungen suchen',
    'Search methods': 'Methoden suchen',
    'You can do this': 'Du kannst das',
    'No exercises found': 'Keine Übungen gefunden',
    'Try a different search or upload exercises.': 'Andere Suche versuchen oder Übungen hochladen.',
    Sketch: 'Skizze',
    'How to do it': 'Ausführung',
    'Exercise Details': 'Übungsdetails',
    'No methods found': 'Keine Methoden gefunden',
    'Try another search or upload methods.': 'Andere Suche versuchen oder Methoden hochladen.',
    'Click for details': 'Für Details klicken',
    'What it is': 'Was es ist',
    'Use it for': 'Dafür nutzen',
    'Strength/Hypertrophy': 'Kraft/Hypertrophie',
    AMRAP: 'AMRAP',
    EMOM: 'EMOM',
    'Girl WOD': 'Girl WOD',
    'Hero WOD': 'Hero WOD',
    'Workout Manager is unlocked.': 'Workout-Manager ist freigeschaltet.',
    'Unlock this feature in Profile to access Workout Manager.': 'Schalte die Funktion im Profil frei, um den Workout-Manager zu nutzen.',
    'Buy me a Coffee': 'Kaffee spendieren',
    Rounds: 'Runden',
    On: 'An',
    Off: 'Aus',

    // Cookie banner
    'We use essential cookies/local storage for login, sessions, and preferences. We do not run analytics or marketing cookies.':
      'Wir nutzen nur notwendige Cookies/Local Storage für Login, Sitzungen und Einstellungen. Keine Analyse- oder Marketing-Cookies.',
    'Read more in our': 'Mehr dazu in unserer',
    'Privacy Policy': 'Datenschutzerklärung',
    'Got it': 'Verstanden',

    // General actions
    Save: 'Speichern',
    Cancel: 'Abbrechen',
    Continue: 'Weiter',
    List: 'Liste',
    Today: 'Heute',
    Date: 'Datum',
    Time: 'Uhrzeit',
    Type: 'Typ',
    Notes: 'Notizen',
    Method: 'Methode',
    Plan: 'Plan',
    User: 'Benutzer',
    Username: 'Benutzername',
    Account: 'Konto',
    Free: 'Kostenlos',
    'Free plan': 'Kostenloser Plan',
    'Add method': 'Methode hinzufügen',
    'Remove method': 'Methode entfernen',
    'Expand exercises': 'Übungen ausklappen',
    'Collapse exercises': 'Übungen einklappen',
    'Expand methods': 'Methoden ausklappen',
    'Collapse methods': 'Methoden einklappen',
    Equipped: 'Ausgestattet',
    Calendar: 'Kalender',
    'Next month': 'Nächster Monat',
    'Previous month': 'Vorheriger Monat',
    'Switch to Calendar view': 'Zur Kalenderansicht wechseln',
    'Switch to List view': 'Zur Listenansicht wechseln',

    // Planner / training creation
    'Workout Name': 'Workout-Name',
    'Workout name': 'Workout-Name',
    'Enter a plan name': 'Plannamen eingeben',
    'Optional notes…': 'Optionale Notizen…',
    'Sets / Reps / Weights': 'Sätze / Wdh. / Gewicht',
    'Select a date': 'Datum auswählen',
    'Select a time': 'Uhrzeit auswählen',
    'Select at least one training': 'Mindestens ein Training auswählen',
    'Build your workout block by block': 'Erstelle dein Training Block für Block',
    'e.g., Back & Shoulders': 'z.B. Rücken & Schultern',
    'Create All': 'Alle erstellen',
    'Edit Training': 'Training bearbeiten',
    'Start Training': 'Training starten',
    'Training created ✔': 'Training erstellt ✔',
    'Planned training deleted': 'Geplantes Training gelöscht',
    'Time cap is up': 'Zeitlimit abgelaufen',

    // Text planner / AI / OCR
    'Text Planner': 'Text-Planer',
    'AI Generator': 'KI-Generator',
    'Paste or type your workout in plain text': 'Workout als Text eingeben oder einfügen',
    'Let AI build a workout for you': 'Lass die KI ein Training für dich erstellen',
    'Please answer all questions first.': 'Bitte zuerst alle Fragen beantworten.',
    'OCR ready ✔': 'OCR bereit ✔',
    'No exercises detected': 'Keine Übungen erkannt',
    'No exercises to create': 'Keine Übungen zum Erstellen',
    'Exercises could not be matched': 'Übungen konnten nicht zugeordnet werden',
    'No matches': 'Keine Treffer',
    'Training created from text planner.': 'Training aus Text-Planer erstellt.',
    'Workout generated successfully.': 'Workout erfolgreich generiert.',
    'Pro unlocks Text Planner and AI Planner.': 'Pro schaltet Text-Planer und KI-Planer frei.',

    // Subscription / billing
    Subscription: 'Abonnement',
    'Upgrade to Pro': 'Auf Pro upgraden',
    'Upgrade to Coach': 'Auf Coach upgraden',
    'Manage Subscription': 'Abo verwalten',
    'No checkout URL returned': 'Kein Checkout-Link erhalten',
    'Could not start checkout': 'Checkout konnte nicht gestartet werden',
    'Could not open billing portal': 'Abrechnungsportal konnte nicht geöffnet werden',
    'Subscription activated!': 'Abonnement aktiviert!',
    'Weekly limit reached. Upgrade to Pro for unlimited access.':
      'Wochenlimit erreicht. Upgrade auf Pro für unbegrenzten Zugang.',

    // Account / profile
    'Sign Out': 'Abmelden',
    'Sign in with Apple': 'Mit Apple anmelden',
    'Delete Account': 'Konto löschen',
    'Delete my account': 'Mein Konto löschen',
    'Deleting…': 'Löschen…',
    'This will permanently delete:': 'Dies löscht dauerhaft:',
    'Your profile and all settings': 'Dein Profil und alle Einstellungen',
    'All planned and completed trainings': 'Alle geplanten und abgeschlossenen Trainings',
    'Any workout plans you created': 'Alle von dir erstellten Trainingspläne',
    'This action cannot be undone.': 'Diese Aktion kann nicht rückgängig gemacht werden.',
    'to confirm': 'zum Bestätigen',
    'App Version': 'App-Version',
    'Use Guide': 'Anleitung',
    'Change Log': 'Änderungsprotokoll',
    'Terms of Service': 'Nutzungsbedingungen',
    'My Friend ID': 'Meine Freundes-ID',

    // Workout plans / shop
    'Failed to load workout plans': 'Trainingspläne konnten nicht geladen werden',
    'Failed to load plan details': 'Plandetails konnten nicht geladen werden',
    'Failed to load planner data': 'Planerdaten konnten nicht geladen werden',
    'Failed to load methods/exercises': 'Methoden/Übungen konnten nicht geladen werden',
    'Back': 'Zurück',
    'Reschedule': 'Neu planen',
    'Rescheduling…': 'Wird neu geplant…',
    'Reschedule this workout': 'Dieses Workout neu planen',
    'Create a new planned workout from this session': 'Neues geplantes Workout aus dieser Einheit erstellen',
    'Click to view details': 'Für Details tippen',
    'Completed Training Details': 'Details des abgeschlossenen Trainings',
    // Booking (Coach Plans)
    'Join': 'Beitreten',
    'Connect to Coach': 'Mit Coach verbinden',
    'Connect': 'Verbinden',
    'Saved Coaches': 'Gespeicherte Coaches',
    'Remove': 'Entfernen',
    'Select your Coach': 'Wähle deinen Coach',
    'Choose Coach': 'Coach auswählen',
    'Change': 'Ändern',
    'Address': 'Adresse',
    'Marked as ready': 'Als bereit markiert',
    'Start planned training': 'Geplantes Training starten',
    'You can find the Coach Friend ID in your Workout Manager from your Coach (ask your Trainer).': 'Die Coach-Freundes-ID findest du im Workout Manager deines Coaches (frag deinen Trainer).',
    'Add your Coach Friend ID in the Join tab to view Coach Plans.': 'Füge im Beitreten-Tab deine Coach-Freundes-ID hinzu, um Coach-Pläne zu sehen.',
    'The Workout is not started. Please wait for the Coach': 'Das Training wurde noch nicht gestartet. Bitte warte auf den Coach.',
    'View details': 'Details ansehen',
    'Buy Workout Plan': 'Trainingsplan kaufen',
    'Apply Plan': 'Plan übernehmen',
    'Bought': 'Gekauft',
    'Start date': 'Startdatum',
    'Failed to delete': 'Löschen fehlgeschlagen',
    'Failed to save equipment.': 'Ausstattung konnte nicht gespeichert werden.',
    'Failed to save username': 'Benutzername konnte nicht gespeichert werden',
    'No workouts scheduled': 'Keine Workouts geplant',
    'Please sign in to view planned trainings': 'Bitte einloggen, um geplante Trainings zu sehen',
    'Please sign in to view workout plans': 'Bitte einloggen, um Trainingspläne zu sehen',
    'Please sign in first.': 'Bitte zuerst einloggen.',
    'workout / week': 'Training / Woche',
    left: 'übrig',

    // Errors
    'Network error. Please try again.': 'Netzwerkfehler. Bitte erneut versuchen.',
    'Something went wrong. Please try again.': 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',

    // Update banner
    'A new update is available': 'Ein Update ist verfügbar',
    'There is a new update available': 'Es ist ein neues Update verfügbar',
    'Refresh app to load the newest version': 'App aktualisieren, um die neueste Version zu laden',

    // Notifications
    'Workout plan purchased! You can apply it now.': 'Trainingsplan gekauft! Du kannst ihn jetzt anwenden.',
    'Purchase cancelled': 'Kauf abgebrochen',
    'Thank you for your Support!': 'Danke fuer deine Unterstuetzung!',
    'Thank you for purchasing!': 'Danke fuer deinen Kauf!',
    'You have a new friend request': 'Du hast eine neue Freundschaftsanfrage',
    'invited you to a partner workout': 'hat dich zu einem Partner-Workout eingeladen',
    'Partner workout': 'Partner-Workout',
    // AI Planner → Quick workout, Advanced options
    'Warm-up': 'Aufwärmen',
    'Advanced': 'Erweitert',
    'Equipment (multiple)': 'Equipment (Mehrfachauswahl)',
    'Strength': 'Kraft',
    'Hypertrophy': 'Hypertrophie',
    'Endurance': 'Ausdauer',
    'Mixed': 'Gemischt',
    'Body part': 'Körperbereich',
    'Full-Body': 'Ganzkörper',
    'Upper Body': 'Oberkörper',
    'Lower Body': 'Unterkörper',
    'Push': 'Push (Drücken)',
    'Pull': 'Pull (Ziehen)',
    'Legs': 'Beine',
    'Focus exercise (optional)': 'Fokus-Übung (optional)',
    'e.g. Back Squat, Bench Press, Snatch': 'z. B. Back Squat, Bench Press, Snatch',
    'Pick from the list so the AI builds the session around it.':
      'Wähle aus der Liste, damit die KI das Workout darum herum aufbaut.',
    'Pick your equipment, goal and focus — the AI builds a single workout to match.':
      'Wähle Equipment, Ziel und Fokus — die KI baut ein passendes Workout.',
    'No new notifications.': 'Keine neuen Benachrichtigungen.',
    'Last messages': 'Letzte Nachrichten',
    'No messages': 'Keine Nachrichten',
    'Please update Probell in the App Store.': 'Bitte aktualisiere Probell im App Store.',
    'Please update Probell in the Play Store.': 'Bitte aktualisiere Probell im Play Store.',
    'Please update Probell in your app store.': 'Bitte aktualisiere Probell in deinem App Store.',
    'please click on the Probell icon in the upper left corner to update/refresh.':
      'bitte klicke oben links auf das Probell-Symbol, um zu aktualisieren.',
    'Coach workout has started now': 'Coach-Workout hat jetzt begonnen',
    'Scheduled workout': 'Geplantes Workout',
    // AI Training Plan
    'AI Planner': 'KI-Planer',
    'Evidence-based weekly plan built by AI': 'Evidenzbasierter Wochenplan, erstellt von KI',
    'Close': 'Schließen',
    'Copy': 'Kopieren',
    'Copied': 'Kopiert',
    'Generate another plan': 'Weiteren Plan erstellen',
    'Answer a few questions — equipment is taken from your profile. The AI builds an evidence-based weekly plan.':
      'Beantworte ein paar Fragen — die Ausrüstung kommt aus deinem Profil. Die KI erstellt einen evidenzbasierten Wochenplan.',
    'Goal': 'Ziel',
    'Experience': 'Erfahrung',
    'Years lifting': 'Jahre Training',
    'optional': 'optional',
    'Days / week': 'Tage / Woche',
    'Session length': 'Trainingsdauer',
    'Nutrition phase': 'Ernährungsphase',
    'Priority muscles': 'Prioritäts-Muskeln',
    'e.g. back, shoulders (optional)': 'z. B. Rücken, Schultern (optional)',
    'Concurrent conditioning': 'Begleitendes Konditionstraining',
    'e.g. 3 CrossFit metcons/week (optional)': 'z. B. 3 CrossFit-Metcons/Woche (optional)',
    'Injuries / limitations': 'Verletzungen / Einschränkungen',
    'Anything else': 'Sonstiges',
    'Building your plan…': 'Dein Plan wird erstellt…',
    'Generate plan': 'Plan erstellen',
    'This can take up to a minute.': 'Das kann bis zu einer Minute dauern.',
    'Could not generate plan. Please try again.': 'Plan konnte nicht erstellt werden. Bitte versuche es erneut.',
    'Could not create workouts. Please try again.': 'Workouts konnten nicht erstellt werden. Bitte versuche es erneut.',
    'Could not remove the plan. Please try again.': 'Plan konnte nicht entfernt werden. Bitte versuche es erneut.',
    'Answer a few questions. The AI builds a progressive mesocycle and can add it to your planner.':
      'Beantworte ein paar Fragen. Die KI erstellt einen progressiven Mesozyklus und kann ihn zu deinem Planer hinzufügen.',
    'Equipment': 'Ausrüstung',
    'My equipment': 'Meine Ausrüstung',
    'Full gym (all equipment)': 'Volles Studio (alle Geräte)',
    'AI plans left this month': 'KI-Pläne diesen Monat übrig',
    'Monthly AI plan limit reached. Resets:': 'Monatliches KI-Plan-Limit erreicht. Reset:',
    'Full plan': 'Voller Plan',
    'Quick workout': 'Schnelles Workout',
    'Pick one piece of equipment — the AI builds a single full-body workout you can do with just that.': 'Wähle ein Trainingsgerät — die KI erstellt ein einzelnes Ganzkörper-Workout, das du nur damit machen kannst.',
    // AI Planner → Screenshot import
    'Read image': 'Bild einlesen',
    'Image': 'Bild',
    '1 image attached — tap “Read image” to read it.': '1 Bild angehängt — tippe auf „Bild einlesen“, um es auszulesen.',
    'images attached — tap “Read image” to read them.': 'Bilder angehängt — tippe auf „Bild einlesen“, um sie auszulesen.',
    'Reading your image…': 'Dein Bild wird gelesen …',
    'You can lock your screen — it keeps going.': 'Du kannst den Bildschirm sperren — es läuft weiter.',
    'Reading your image — about a minute. Safe to close.': 'Dein Bild wird gelesen — etwa eine Minute. Du kannst die App schließen.',
    'That import is no longer available. Please try again.': 'Dieser Import ist nicht mehr verfügbar. Bitte versuche es erneut.',
    'Still reading — this is taking longer than usual. Reopen the AI Planner in a moment to pick it up.':
      'Wird noch gelesen — das dauert länger als üblich. Öffne den KI-Planer in einem Moment erneut, um fortzusetzen.',
    'This image was already read — picking it up again costs nothing.':
      'Dieses Bild wurde schon eingelesen — es fortzusetzen kostet nichts.',
    // 'Continue' is already translated further up (line ~400, unquoted key).
    'Discard it': 'Verwerfen',
    'Photograph a whiteboard or pick a screenshot of a workout — the AI reads it and adds it to Self Created. Anything it could not read is listed in the notes.':
      'Fotografiere ein Whiteboard oder wähle einen Screenshot eines Workouts — die KI liest es aus und legt es unter „Selbst erstellt“ ab. Was sie nicht lesen konnte, steht in den Notizen.',
    'Images': 'Bilder',
    'Add image': 'Bild hinzufügen',
    'Up to 3 images of the same workout — e.g. a wide board photographed in halves.':
      'Bis zu 3 Bilder desselben Workouts — z. B. ein breites Board in zwei Hälften fotografiert.',
    'If a date is printed on the image, it replaces this one — you can still change it.':
      'Steht ein Datum im Bild, ersetzt es dieses — du kannst es trotzdem ändern.',
    'You can attach up to 3 images of the same workout.': 'Du kannst bis zu 3 Bilder desselben Workouts anhängen.',
    'That file could not be read as an image. Try a screenshot or a JPEG photo.':
      'Diese Datei konnte nicht als Bild gelesen werden. Versuche einen Screenshot oder ein JPEG-Foto.',
    'Could not read that screenshot. Please try again.': 'Der Screenshot konnte nicht gelesen werden. Bitte versuche es erneut.',
    'Title': 'Titel',
    'Please check': 'Bitte prüfen',
    'Not in the exercise catalog:': 'Nicht im Übungskatalog:',
    'kept in the notes only.': 'nur in den Notizen vermerkt.',
    'This list is saved into the workout’s notes too.': 'Diese Liste wird auch in den Notizen des Workouts gespeichert.',
    // "Please add this exercise" — the queue in front of the shared catalog.
    'Request to add': 'Aufnahme anfragen',
    'Sending…': 'Wird gesendet …',
    'Requested ✓': 'Angefragt ✓',
    // Replaces "Already in the catalog — reopen to use it.": the create step looks
    // every name up against the live catalog, so an approval that lands while the
    // import is waiting is picked up on save. Telling the user to read the image
    // again would have cost them another AI credit for nothing.
    'Added to the catalog:': 'In den Katalog aufgenommen:',
    'it is linked when you save.': 'wird beim Speichern verknüpft.',
    'Could not send — retry': 'Senden fehlgeschlagen — erneut versuchen',
    'A request goes to the catalog owner. You’ll get a notification once it’s added.':
      'Die Anfrage geht an die Katalogpflege. Du bekommst eine Mitteilung, sobald die Übung aufgenommen ist.',
    // The reviewer's side of the same queue (ExerciseRequestsAdmin).
    'Exercise requests': 'Übungsanfragen',
    'Exercise name': 'Übungsname',
    // Scoped keys, not a bare 'Add'/'Decline': those two strings are already used
    // by the friend list, the partner invite and the planner, where they mean
    // something else and currently fall through to English on purpose.
    'Add to catalog': 'Aufnehmen',
    'Decline request': 'Ablehnen',
    'requests': 'Anfragen',
    'as read:': 'gelesen als:',
    'Reason (sent to the requester)': 'Grund (geht an die anfragende Person)',
    'Send': 'Senden',
    'no equipment tags': 'keine Equipment-Tags',
    'Could not add that exercise.': 'Die Übung konnte nicht aufgenommen werden.',
    'Could not reject that request.': 'Die Anfrage konnte nicht abgelehnt werden.',
    'sets not stated': 'Sätze nicht angegeben',
    'reps not stated': 'Wiederholungen nicht angegeben',
    'not in catalog': 'nicht im Katalog',
    'Saved to Self Created on the chosen date.': 'Wird am gewählten Datum unter „Selbst erstellt“ gespeichert.',
    'It’s in Self Created on the chosen date. The notes hold the original text and anything worth checking.':
      'Es liegt am gewählten Datum unter „Selbst erstellt“. Die Notizen enthalten den Originaltext und alles, was du prüfen solltest.',
    'Dumbbells': 'Kurzhanteln',
    'Barbell': 'Langhantel',
    'Kettlebells': 'Kettlebells',
    'Prepare': 'Vorbereiten',
    'Other equipment': 'Weiteres Equipment',
    'Equipment needed for this session': 'Benötigtes Equipment für diese Einheit',
    'No special equipment — bodyweight session.': 'Kein spezielles Equipment – Körpergewichtstraining.',
    'per side': 'pro Seite',
    'Plates': 'Scheiben',
    'bar': 'Stange',
    'empty bar': 'leere Stange',
    'Load not set': 'Gewicht nicht festgelegt',
    'Weights not set': 'Gewichte nicht festgelegt',
    '(per dumbbell)': '(pro Kurzhantel)',
    'Green = already in Your Equipment': 'Grün = bereits in deiner Ausstattung',
    // Prepare — "Other equipment" chip labels (namespaced to avoid clashing with
    // generic UI words; keys mirror prettyEquipment() output in lib/equipment.ts)
    'equipment:Resistance band': 'Widerstandsband',
    'equipment:Mini band': 'Mini-Band',
    'equipment:Pull-up bar': 'Klimmzugstange',
    'equipment:Plyo box': 'Plyobox',
    'equipment:Bench': 'Hantelbank',
    'equipment:Incline bench': 'Schrägbank',
    'equipment:Wall ball': 'Wall Ball',
    'equipment:Medicine ball': 'Medizinball',
    'equipment:Slam ball': 'Slam Ball',
    'equipment:Jump rope': 'Springseil',
    'equipment:Climbing rope': 'Kletterseil',
    'equipment:Rower': 'Rudergerät',
    'equipment:Ski erg': 'Ski-Ergometer',
    'equipment:Bike': 'Bike',
    'equipment:Assault bike': 'Assault Bike',
    'equipment:Echo bike': 'Echo Bike',
    'equipment:Cable machine': 'Kabelzug',
    'equipment:Foam roller': 'Faszienrolle',
    'equipment:Ab wheel': 'Bauchroller',
    'equipment:Gymnastic rings': 'Turnringe',
    'equipment:Sled': 'Schlitten',
    'equipment:Trap bar': 'Trap-Bar',
    'equipment:Landmine': 'Landmine',
    'equipment:Tire': 'Reifen',
    // Left untranslated on purpose: German gym usage is "Yoke", and the literal
    // "Joch" is the one oxen wear.
    'equipment:Yoke': 'Yoke',
    'equipment:GHD machine': 'GHD-Maschine',
    'equipment:Back-extension bench': 'Rückenstrecker-Bank',
    'equipment:Parallettes': 'Parallettes',
    'Resistance bands': 'Widerstandsbänder',
    'Bodyweight': 'Körpergewicht',
    'Date': 'Datum',
    'Adds one workout to your planner on the chosen date.': 'Fügt deinem Planer ein Workout am gewählten Datum hinzu.',
    'Workout added': 'Workout hinzugefügt',
    'Planner': 'Planer',
    'Search planned': 'Geplante durchsuchen',
    'Search plans': 'Pläne durchsuchen',
    'No plans match your search.': 'Keine Pläne passen zu deiner Suche.',
    'All sources': 'Alle Quellen',
    'Created by': 'Erstellt von',
    'Plan length': 'Plan-Länge',
    'Any length': 'Beliebige Länge',
    'No planned trainings match your filters.': 'Keine geplanten Trainings passen zu deinen Filtern.',
    'No planned trainings in this category.': 'Keine geplanten Trainings in dieser Kategorie.',
    'Filter and sort': 'Filtern und sortieren',
    'Sort by': 'Sortieren nach',
    'Name': 'Name',
    'Toggle sort direction': 'Sortierrichtung umschalten',
    'Oldest first': 'Älteste zuerst',
    'Newest first': 'Neueste zuerst',
    'Add completed workout': 'Abgeschlossenes Workout hinzufügen',
    'Save': 'Speichern',
    'Total time': 'Gesamtzeit',
    'Exercises': 'Übungen',
    'Exercise / station': 'Übung / Station',
    'reps': 'Wdh.',
    'sec': 'Sek.',
    'Hold (sec)': 'Halten (Sek.)',
    'Add exercise': 'Übung hinzufügen',
    'Notes': 'Notizen',
    'Log a workout you did off-app, with its total time, so you can repeat it and compare next time.': 'Erfasse ein Workout, das du außerhalb der App gemacht hast, mit Gesamtzeit – so kannst du es wiederholen und beim nächsten Mal vergleichen.',
    'e.g. Hyrox Simulation': 'z. B. Hyrox-Simulation',
    'Please enter a workout name.': 'Bitte gib einen Workout-Namen ein.',
    'Please enter the total time.': 'Bitte gib die Gesamtzeit ein.',
    'Could not save. Please try again.': 'Konnte nicht gespeichert werden. Bitte versuche es erneut.',
    'Workout added to Completed.': 'Workout zu „Abgeschlossen“ hinzugefügt.',
    'Primary:': 'Primär:',
    'Secondary:': 'Sekundär:',
    'Next': 'Nächste',
    // Muscle groups (namespaced to avoid clashing with UI words like "Back")
    'muscle:Back': 'Rücken',
    'muscle:Biceps': 'Bizeps',
    'muscle:Calves': 'Waden',
    'muscle:Chest': 'Brust',
    'muscle:Core': 'Rumpf',
    'muscle:Forearms': 'Unterarme',
    'muscle:Front Shoulder': 'Vordere Schulter',
    'muscle:Full Body': 'Ganzkörper',
    'muscle:Glutes': 'Gesäß',
    'muscle:Hamstrings': 'Beinbeuger',
    'muscle:Hip Flexors': 'Hüftbeuger',
    'muscle:Inner Thigh': 'Innerer Oberschenkel',
    'muscle:Lats': 'Latissimus',
    'muscle:Legs': 'Beine',
    'muscle:Lower Back': 'Unterer Rücken',
    'muscle:Neck': 'Nacken',
    'muscle:Obliques': 'Schräge Bauchmuskeln',
    'muscle:Quads': 'Quadrizeps',
    'muscle:Rear Shoulder': 'Hintere Schulter',
    'muscle:Rotator Cuff': 'Rotatorenmanschette',
    'muscle:Shoulders': 'Schultern',
    'muscle:Side Shoulder': 'Seitliche Schulter',
    'muscle:Traps': 'Trapez',
    'muscle:Triceps': 'Trizeps',
    'muscle:Upper Back': 'Oberer Rücken',
    'Log completed': 'Abgeschlossenes erfassen',
    'Save completed': 'Abgeschlossenes speichern',
    'Avg BPM': 'Ø BPM',
    'Monthly AI plan limit reached.': 'Monatliches KI-Plan-Limit erreicht.',
    'Available again in': 'Wieder verfügbar in',
    'day': 'Tag',
    'days': 'Tagen',
    'Buy 10 more': '10 weitere kaufen',
    'You can buy more AI generations in the iOS app.': 'Mehr KI-Generierungen kannst du in der iOS-App kaufen.',
    'Purchase failed. Please try again.': 'Kauf fehlgeschlagen. Bitte erneut versuchen.',
    'Completed workout saved.': 'Abgeschlossenes Workout gespeichert.',
    'It’s in your planner on the chosen date. Adjust it any time.': 'Es ist am gewählten Datum in deinem Planer. Du kannst es jederzeit anpassen.',
    'Length': 'Dauer',
    'weeks': 'Wochen',
    'Creates': 'Erstellt',
    'workouts': 'Workouts',
    'from': 'ab',
    'Day': 'Tag',
    'sets': 'Sätze',
    'set': 'Satz',
    'Sets shown as week 1 → final (deload) week.': 'Sätze von Woche 1 → letzte (Deload-)Woche.',
    'Created': 'Erstellt',
    'They’re in your planner across the mesocycle. Adjust times or exercises any time.':
      'Sie liegen über den Mesozyklus verteilt in deinem Planer. Zeiten oder Übungen kannst du jederzeit anpassen.',
    'Adding…': 'Wird hinzugefügt…',
    'Add to planner': 'Zum Planer hinzufügen',
    'Done': 'Fertig',
    // IGYG knock-down page
    'Knock down reps': 'Wdh. abbauen',
    'Knock down': 'Abbauen',
    'remaining': 'übrig',
    'Undo': 'Rückgängig',
    'Counters': 'Zähler',
    'Page': 'Seite',
    // Standalone Timer tool
    'Timer': 'Timer',
    'AMRAP, EMOM, Tabata & more': 'AMRAP, EMOM, Tabata & mehr',
    'Stopwatch': 'Stoppuhr',
    // Planner → Preview tab
    'Preview': 'Vorschau',
    'Still missing': 'Fehlt noch',
    'No exercises yet': 'Noch keine Übungen',
    'Dropset': 'Dropsatz',
    'Complex': 'Komplex',
    'Countdown': 'Countdown',
    'Tabata': 'Tabata',
    'For Time': 'Auf Zeit',
    'Interval': 'Intervall',
    'Work': 'Belastung',
    'Rest': 'Pause',
    'Time cap': 'Zeitlimit',
    'Duration': 'Dauer',
    'Total': 'Gesamt',
    'Start': 'Start',
    'Pause': 'Pause',
    'Resume': 'Fortsetzen',
    'Get ready': 'Mach dich bereit',
    'Start countdown': 'Start-Countdown',
    'Custom': 'Eigene',
    'Step': 'Schritt',
    'Add step': 'Schritt hinzufügen',
    'Repeat sequence': 'Sequenz wiederholen',
    'Saved timers': 'Gespeicherte Timer',
    'Save timer': 'Timer speichern',
    'Name this timer': 'Timer benennen',
    'Move up': 'Nach oben',
    'Move down': 'Nach unten',
    'optional': 'optional',
    'Counts up from 0:00. Tap Start.': 'Zählt ab 0:00 hoch. Tippe auf Start.',
    'Fill in every field to start.': 'Fülle alle Felder aus, um zu starten.',
    'Reset to classic Tabata (20/10 × 8)': 'Auf klassisches Tabata zurücksetzen (20/10 × 8)',
    'Remove these workouts': 'Diese Workouts entfernen',
    // Barbell calculator
    'Barbell calculator': 'Hantelrechner',
    'Drag plates onto the bar to get the total': 'Scheiben auf die Stange ziehen und Gesamtgewicht ablesen',
    "Men's bar": 'Herrenhantel',
    "Women's bar": 'Damenhantel',
    'Bar': 'Stange',
    'Per side': 'Pro Seite',
    'Drag a plate onto the bar': 'Zieh eine Scheibe auf die Stange',
    'Drag a plate on, or type the weight above': 'Scheibe aufziehen oder oben das Gewicht eintippen',
    'Tap a plate to remove it': 'Zum Entfernen auf eine Scheibe tippen',
    'Clear bar': 'Stange leeren',
    'The bar is full.': 'Die Stange ist voll.',
    'Total weight': 'Gesamtgewicht',
    'not possible': 'nicht möglich',
    // AI Training Plan — dropdown option labels (values stay English for the AI)
    'hypertrophy': 'Hypertrophie',
    'strength': 'Kraft',
    'endurance': 'Ausdauer',
    'mixed': 'Gemischt',
    'general fitness': 'Allgemeine Fitness',
    'beginner': 'Anfänger',
    'intermediate': 'Mittelstufe',
    'advanced': 'Fortgeschritten',
    'maintenance': 'Erhaltung',
    'caloric surplus': 'Kalorienüberschuss',
    'caloric deficit': 'Kaloriendefizit',
    'Purchase received — if it doesn’t activate, tap Restore Purchases.':
      'Kauf erhalten — falls er sich nicht aktiviert, tippe auf „Käufe wiederherstellen“.',
    'Subscriptions can only be purchased in the Probell iOS app.':
      'Abonnements können nur in der Probell iOS-App gekauft werden.',
    'Manage your subscription in the Probell iOS app.':
      'Verwalte dein Abonnement in der Probell iOS-App.',
    'Restore Purchases': 'Käufe wiederherstellen',
    'Restoring…': 'Wird wiederhergestellt…',
    'Purchases restored.': 'Käufe wiederhergestellt.',
    'Could not restore purchases.': 'Käufe konnten nicht wiederhergestellt werden.',
  },
};

function getInitialLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (stored && AVAILABLE.includes(stored)) return stored;
  const nav = navigator.language?.toLowerCase() || '';
  if (nav.startsWith('de')) return 'de';
  return DEFAULT_LOCALE;
}

const LocaleContext = React.createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key: string, fallback?: string) => fallback ?? key,
  available: AVAILABLE,
});

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<Locale>(getInitialLocale);

  React.useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const setLocale = React.useCallback((next: Locale) => {
    setLocaleState(AVAILABLE.includes(next) ? next : DEFAULT_LOCALE);
  }, []);

  const t = React.useCallback(
    (key: string, fallback?: string) => {
      const clean = key.trim();
      const translated = translations[locale]?.[clean];
      if (translated) return translated;
      return fallback ?? clean;
    },
    [locale]
  );

  const value = React.useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      t,
      available: AVAILABLE,
    }),
    [locale, setLocale, t]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return React.useContext(LocaleContext);
}
